import React, { useEffect, useRef, useState, useMemo } from 'react';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined';


// shared - components
import Table from '../../../shared/components/Table/Table';
import LoaderContent from '../../../shared/components/Loader/LoaderContent';
import ConfirmDialog from '../../../shared/components/ConfirmDialog/ConfirmDialog';
import Toast from '../../../shared/components/Toast/Toast';
import MoreOptions from '../../../shared/components/MoreOptions/MoreOptions';
import { log } from '../../../shared/utils/console.js';

// SDK
import { ThinkificService } from '@sdk-point/talisis';

// config
import { catalogConfigs } from '../ColumnsConfig.jsx';

// @local
import { useStyles } from '../../../assets/styles/jss/pages/microcredentials/useStyles';

const ThinkificServiceSDK = new ThinkificService(
  process.env.REACT_APP_ENV_V2,
  process.env.REACT_APP_X_API_KEY_V2
);

function Actions({ item, onClickEdit, onClickDelete }) {
  const classes = useStyles();
  const options = [
    { label: 'Editar', fun: () => onClickEdit(item), icon: <EditOutlinedIcon className={classes.actionIcon} /> },
    { label: 'Eliminar', fun: () => onClickDelete(item), icon: <DeleteOutlinedIcon className={classes.actionIcon} /> },
  ];
  return <MoreOptions options={options} ExAction={true} />;
}

const AllRequests = ({
  catalogType,
  canReadWrite,
  page = 1,
  pageSize = 10,
  setPagination,
  refreshTrigger,
  filters = {},
  searchText = "",
  onEditItem,
  onHasRecordsChange,
}) => {
  const classes = useStyles();
  const { columns, entityName } = catalogConfigs[catalogType];

  const [state, setState] = useState({ loading: true, requests: [] });
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [itemSelected, setItemSelected] = useState({});
  const [toastProps, setToastProps] = useState({});
  const [dialogProps, setDialogProps] = useState({ title: '', text: '', open: false });
  const [searchTerm, setSearchTerm] = useState('');

  const isFetching = useRef(false);

  // Actualizamos searchTerm localmente
  useEffect(() => {
    setSearchTerm(searchText || '');
  }, [searchText]);

  // Filtrado local por microcredential_desc y badge_desc
  useEffect(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      setFilteredRequests(state.requests);
    } else {
      setFilteredRequests(
        state.requests.filter(
          item =>
            item.microcredential_desc?.toLowerCase().includes(term) ||
            item.badge_desc?.toLowerCase().includes(term)
        )
      );
    }
  }, [searchTerm, state.requests]);

  const stableFilters = useMemo(() => filters, [filters.status, filters.category]);
  useEffect(() => {
    getRequests(stableFilters);
  }, [stableFilters]);

  useEffect(() => {
    if (onHasRecordsChange) {
      onHasRecordsChange(filteredRequests.length > 0);
    }
  }, [filteredRequests]);

  // Función para obtener datos
  const getRequests = async (activeFilters = {}) => {
    if (isFetching.current) return;
    isFetching.current = true;
    setState(prev => ({ ...prev, loading: true }));

    try {
      const response = await ThinkificServiceSDK.getCatalogs({
        CatalogType: catalogType,
        page,
        pageSize,
        ...activeFilters,
      });

      const requests = response.data.map(it => ({
        ...it,
        id: String(it.id),
        entityName,
        actions: (
          <Actions
            item={{ ...it, id: String(it.id), entityName }}
            loading={false}
            canReadWrite={canReadWrite}
            onClickEdit={handleClickEdit}
            onClickDelete={handleClickDelete}
          />
        ),
      }));

      setState({ loading: false, requests });
      setFilteredRequests(requests); // Inicialmente mostrar todos

      if (catalogType === "rules" && response.pagination) {
        setPagination(response.pagination);
      }
    } catch (e) {
      log('error', e);
      setState({ loading: false, requests: [] });
      setFilteredRequests([]);
    } finally {
      isFetching.current = false;
    }
  };

  // Fetch de datos: solo cuando cambian page, pageSize, filters o refreshTrigger
  useEffect(() => {
    getRequests(filters);
  }, [page, pageSize, refreshTrigger, JSON.stringify(filters)]);


  const handleClickEdit = (item) => {
    if (onEditItem) {
      let formData = {};

      switch (item.entityName) {
        case "Bundle":
          formData = { id: item.id, ou: item.uo || "", name: item.name || "", description: item.description || "" };
          break;
        case "Microcredencial":
          formData = { id: item.id, name: item.name || "", templatePoc: item.template_poc || "", credentialId: item.credential_id || "" };
          break;
        case "Insignia":
          formData = { id: item.id, name: item.name || "", templatePoc: item.template_poc || "", credentialId: item.credential_id || "" };
          break;
        case "Regla":
          formData = { id: item.id, bundleId: item.bundle_id || "", microcredentialId: item.microcredential_id || "", badgeId: item.badge_id || "", assignmentId: item.assignment_id || "", courseId: item.course_id || "" };
          break;
        default:
          console.warn("EntityName no reconocido:", item.entityName);
          return;
      }

      onEditItem(formData);
      return;
    }
  };

  const handleClickDelete = row => {
    setItemSelected(row);
    setDialogProps({
      open: true,
      title: `Eliminar ${entityName}`,
      titleButtonClose: 'Cancelar',
      titleButtonOk: 'Eliminar',
      text: (
        <div style={{ textAlign: "center" }}>
          ¿Está seguro de querer eliminar el registro?
        </div>
      )
    });
  };

  const handleClose = () => setDialogProps({ ...dialogProps, open: false });

  const handleConfirm = async () => {
    setState(prev => ({ ...prev, loading: true }));

    try {
      const payloadDelete = {
        CatalogType: catalogType,
        id: itemSelected.id,
        is_active: 0,
      };

       await ThinkificServiceSDK.updateCatalogs(payloadDelete);

      setToastProps({ severity: "success", open: true, message: "La acción se realizó correctamente", });

      setTimeout(() => setToastProps({ message: "", open: false }), 3000);

      await getRequests(filters);
    } catch (error) {
      console.error("Error al guardar registro", error);
      setToastProps({ severity: "error", open: true, message: "No se puede eliminar: hay una configuración activa asociada" });
    } finally {
      setState(prev => ({ ...prev, loading: false }));
      setDialogProps(prev => ({ ...prev, open: false }));
    }
  };

  const handleFinishedToast = () => setToastProps({ message: '', open: false });

  return (
    <LoaderContent minHeight={200}>
      {state.loading && <p>Cargando solicitudes...</p>}
      {!state.loading && filteredRequests.length === 0 && <p>No hay registros disponibles.</p>}
      {!state.loading && filteredRequests.length > 0 && (
        <div className={classes.catalogsTable}>
          <Table columns={columns} rows={filteredRequests} />
        </div>
      )}
      <ConfirmDialog {...dialogProps} onConfirm={handleConfirm} onClose={handleClose} />
      <Toast {...toastProps} onFinished={handleFinishedToast} />
    </LoaderContent>
  );
};

export default AllRequests;
